import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Users, Plus, Minus, AlertTriangle, X, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SeatManagementCard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [delta, setDelta] = useState(1);
  const [working, setWorking] = useState(false);

  const fetchSeats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/subscription/seats`, { withCredentials: true });
      setState(res.data);
    } catch {
      // No active plan yet — just hide card
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSeats(); }, [fetchSeats]);

  const submitAdd = async () => {
    setWorking(true);
    try {
      const res = await axios.post(`${API_URL}/subscription/seats/add`, { count: delta }, { withCredentials: true });
      toast.success(`+${delta} användare tillagd. Nytt pris: ${Number(res.data.new_monthly_price).toLocaleString('sv-SE')} kr/mån`);
      setShowAddModal(false);
      setDelta(1);
      fetchSeats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte lägga till användare');
    } finally {
      setWorking(false);
    }
  };

  const submitRemove = async () => {
    setWorking(true);
    try {
      const res = await axios.post(`${API_URL}/subscription/seats/remove`, { count: delta }, { withCredentials: true });
      const date = new Date(res.data.effective_at).toLocaleDateString('sv-SE');
      toast.success(`Borttagning schemalagd till ${date} (1 mån uppsägning).`);
      setShowRemoveModal(false);
      setDelta(1);
      fetchSeats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort användare');
    } finally {
      setWorking(false);
    }
  };

  const cancelRemoval = async (removalId) => {
    try {
      await axios.post(`${API_URL}/subscription/seats/cancel-removal/${removalId}`, {}, { withCredentials: true });
      toast.success('Borttagning avbruten');
      fetchSeats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte avbryta');
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-[#E8613A] animate-spin" />
      </div>
    );
  }

  if (!state || !state.plan_id) return null;

  const perEmp = state.per_employee_price || 0;

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="seat-management-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E8613A]/10 rounded-xl flex items-center justify-center"><Users size={18} className="text-[#E8613A]" /></div>
          <div>
            <h4 className="text-base font-bold text-foreground">Användare (platser)</h4>
            <p className="text-xs text-muted-foreground">Lägg till eller ta bort när du vill — 1 mån uppsägning på extra användare.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Box label="Totalt" value={`${state.employee_count} st`} testid="seats-total" />
        <Box label="Ingår" value="1 st" testid="seats-included" subtitle="i baspaketet" />
        <Box label="Extra" value={`${state.extra_seats} st`} testid="seats-extra" subtitle={`+${perEmp} kr/mån`} />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-5">
        <Button onClick={() => { setDelta(1); setShowAddModal(true); }} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-9 text-sm" data-testid="add-seat-btn">
          <Plus size={14} className="mr-1.5" /> Lägg till användare
        </Button>
        <Button
          onClick={() => { setDelta(1); setShowRemoveModal(true); }}
          disabled={state.extra_seats <= (state.pending_removals || []).reduce((s, p) => s + p.count, 0)}
          variant="outline"
          className="rounded-full h-9 text-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="remove-seat-btn"
        >
          <Minus size={14} className="mr-1.5" /> Ta bort användare
        </Button>
      </div>

      {state.pending_removals && state.pending_removals.length > 0 && (
        <div className="mt-5 space-y-2">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-bold flex items-center gap-1.5"><Clock size={12} /> Schemalagda borttagningar</p>
          {state.pending_removals.map((p) => (
            <div key={p.id} className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3 flex items-center justify-between" data-testid={`pending-removal-${p.id}`}>
              <div>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  −{p.count} användare den {new Date(p.effective_at).toLocaleDateString('sv-SE')}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">Begärt {new Date(p.requested_at).toLocaleDateString('sv-SE')} — 1 månads uppsägning</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => cancelRemoval(p.id)} className="h-8 text-xs" data-testid={`cancel-removal-${p.id}`}>
                Ångra
              </Button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <SeatModal
          title="Lägg till användare"
          onClose={() => setShowAddModal(false)}
          delta={delta}
          setDelta={setDelta}
          max={50}
          info={`Ny kostnad: +${(delta * perEmp).toLocaleString('sv-SE')} kr/mån. Stripe pro-raterar för innevarande period — du kommer alltså bara att debiteras för dagarna kvar.`}
          confirmLabel="Lägg till"
          confirmTone="primary"
          onConfirm={submitAdd}
          working={working}
          testidPrefix="add"
        />
      )}

      {showRemoveModal && (
        <SeatModal
          title="Ta bort användare"
          onClose={() => setShowRemoveModal(false)}
          delta={delta}
          setDelta={setDelta}
          max={state.extra_seats - (state.pending_removals || []).reduce((s, p) => s + p.count, 0)}
          warning
          info={`Borttagningen aktiveras om 30 dagar (1 månads uppsägning). Du behåller åtkomst och betalar för platsen under uppsägningstiden. Du kan ångra borttagningen tills den aktiverats.`}
          confirmLabel="Schemalägg borttagning"
          confirmTone="danger"
          onConfirm={submitRemove}
          working={working}
          testidPrefix="remove"
        />
      )}
    </div>
  );
};

const Box = ({ label, value, subtitle, testid }) => (
  <div className="bg-muted/30 border border-border rounded-lg p-3" data-testid={testid}>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{label}</p>
    <p className="text-xl font-bold text-foreground mt-0.5">{value}</p>
    {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
  </div>
);

const SeatModal = ({ title, onClose, delta, setDelta, max, info, confirmLabel, confirmTone, onConfirm, working, testidPrefix, warning }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={onClose} data-testid={`${testidPrefix}-modal`}>
    <div className="max-w-md w-full bg-card rounded-2xl border border-border shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[#1A1A1A] text-white px-6 py-4 flex items-center justify-between">
        <h2 className="text-base font-semibold flex items-center gap-2">
          {warning && <AlertTriangle size={16} className="text-amber-400" />}{title}
        </h2>
        <button onClick={onClose} className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setDelta(Math.max(1, delta - 1))} className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center hover:bg-[#E8613A] hover:text-white" data-testid={`${testidPrefix}-decrement`}>
            <Minus size={14} />
          </button>
          <Input type="number" value={delta} onChange={(e) => setDelta(Math.max(1, Math.min(max, parseInt(e.target.value) || 1)))} min={1} max={max} className="w-20 text-center text-xl font-bold h-12" data-testid={`${testidPrefix}-count`} />
          <button onClick={() => setDelta(Math.min(max, delta + 1))} className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center hover:bg-[#E8613A] hover:text-white" data-testid={`${testidPrefix}-increment`}>
            <Plus size={14} />
          </button>
        </div>
        <p className="text-sm text-foreground text-center font-medium">{delta} {delta === 1 ? 'användare' : 'användare'}</p>
        <div className={`${warning ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200' : 'bg-muted/40 border-border'} border rounded-lg p-3`}>
          <p className="text-xs text-muted-foreground leading-relaxed">{info}</p>
        </div>
      </div>
      <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
        <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-cancel`}>
          Avbryt
        </button>
        <Button
          onClick={onConfirm}
          disabled={working}
          className={`h-10 px-5 rounded-full ${confirmTone === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#E8613A] hover:bg-[#D4532F] text-white'}`}
          data-testid={`${testidPrefix}-confirm`}
        >
          {working ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

export default SeatManagementCard;
